import {
	createContext,
	createElement,
	useContext,
	useEffect,
	useState,
	type ReactElement,
	type ReactNode,
} from "react";
import { loadPublicSiteConfig, peekPublicUiOptions } from "@Front/Services/PublicSiteConfig/publicSiteConfigLoader";
import type { LoadedPublicSiteConfig } from "@Front/Services/PublicSiteConfig/publicSiteConfigLoaderTypes";
import type { PublicSitePublicUiOptions } from "@Front/Services/PublicSiteConfig/publicSiteConfigLoaderTypes";

export type PublicUiOptionsState = {
	ready: boolean;
	public_ui: PublicSitePublicUiOptions;
};

type PublicSiteConfigContextValue = {
	/** `null` until `loadPublicSiteConfig` resolves. */
	config: LoadedPublicSiteConfig | null;
};

const PublicSiteConfigContext = createContext<PublicSiteConfigContextValue | null>(null);

function usePublicSiteConfigBootstrap(): PublicSiteConfigContextValue {
	const [config, setConfig] = useState<LoadedPublicSiteConfig | null>(null);

	useEffect(() => {
		let cancelled = false;
		void loadPublicSiteConfig().then((cfg) => {
			if (!cancelled) {
				setConfig(cfg);
			}
		});
		return (): void => {
			cancelled = true;
		};
	}, []);

	return { config };
}

/**
 * Mount once under `_app`: single `loadPublicSiteConfig` call; consumers use {@link usePublicSiteConfigContext}
 * (or hooks derived from it) instead of triggering parallel `loadPublicSiteConfig` effects.
 */
export function PublicSiteConfigProvider({ children }: { readonly children: ReactNode }): ReactElement {
	const value = usePublicSiteConfigBootstrap();
	return createElement(PublicSiteConfigContext.Provider, { value }, children);
}

export function usePublicSiteConfigContext(): PublicSiteConfigContextValue {
	const ctx = useContext(PublicSiteConfigContext);
	if (ctx === null) {
		throw new Error("usePublicSiteConfigContext must be used within PublicSiteConfigProvider");
	}
	return ctx;
}

/**
 * Options `public_ui` depuis le contexte (`PublicSiteConfigProvider`), aligné sur `GET /v1/public/site-config`.
 */
export function usePublicUiOptions(): PublicUiOptionsState {
	const { config } = usePublicSiteConfigContext();
	return {
		ready: config !== null,
		public_ui: config?.public_ui ?? peekPublicUiOptions(),
	};
}
